import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import QRCode from "qrcode.react";
import SupabaseImageAvatar from "../utils/components/SupabaseImageAvatar";
import { RankBeltDisplay } from "../utils/components/belt/BeltRankVisual";

const DARK_SURFACE = "#1E2632";
const DARK_BACKGROUND = "#10141D";
const LIGHT_SURFACE = "#FFFFFF";
const LIGHT_BACKGROUND = "#F4F5F7";

/** Belt palette from docs/Design/Belt palette.pdf. Kids two-tone belts use the saturated color. */
const BELT_PALETTE = {
  white: "#EEEEEE",
  grey: "#8E99A5",
  yellow: "#FFC107",
  orange: "#FF9800",
  green: "#4CAF50",
  blue: "#1A56DB",
  purple: "#7E3AF2",
  brown: "#7E4F31",
  black: "#1A1C23",
  coral: "#E31E24",
};

function beltPaletteColor(belt) {
  const token = String(belt || "").toLowerCase().replace(/[-_\s]/g, "");
  if (!token) return BELT_PALETTE.grey;
  if (token.includes("yellow")) return BELT_PALETTE.yellow;
  if (token.includes("orange")) return BELT_PALETTE.orange;
  if (token.includes("green")) return BELT_PALETTE.green;
  if (token.includes("grey") || token.includes("gray")) return BELT_PALETTE.grey;
  if (token.includes("coral")) return BELT_PALETTE.coral;
  if (token.includes("purple")) return BELT_PALETTE.purple;
  if (token.includes("brown")) return BELT_PALETTE.brown;
  if (token.includes("blue")) return BELT_PALETTE.blue;
  if (token.includes("black")) return BELT_PALETTE.black;
  if (token.includes("white")) return BELT_PALETTE.white;
  return BELT_PALETTE.grey;
}

function hexToRgb(hex) {
  const raw = String(hex || "").replace("#", "");
  const n = parseInt(raw.length === 6 ? raw : "8E99A5", 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Opaque mix so a 30% belt tint never turns the card transparent. */
function mixHex(base, belt, beltAmount) {
  const [br, bg, bb] = hexToRgb(base);
  const [tr, tg, tb] = hexToRgb(belt);
  const keep = 1 - beltAmount;
  const channel = (a, b) => Math.round(a * keep + b * beltAmount);
  return `rgb(${channel(br, tr)}, ${channel(bg, tg)}, ${channel(bb, tb)})`;
}

function roleLine(user, t) {
  const isParent = user?.isParent === true;
  const isStudent = user?.isStudent === true;
  const isChild =
    user?.hasParent === true || user?.parentID != null || user?.parentID2 != null;
  const parts = [];
  if (isStudent || (!isParent && isChild)) parts.push(t("student"));
  if (isParent) parts.push(t("parent"));
  if (parts.length === 0) parts.push(t("student"));
  if (user?.isCoach) parts.push(t("instructor"));
  return parts.join(" · ");
}

function LandingFlipMemberCard({
  user,
  t,
  imageRefreshKey,
  prefersReducedMotion = false,
  isDarkMode = false,
  showBeltRank = true,
}) {
  const [flipped, setFlipped] = useState(false);
  const beltValue = showBeltRank ? user?.belt ?? user?.beltColor ?? user?.belt_color : null;
  const beltColor = beltPaletteColor(beltValue);
  const clubName = user?.clubname || user?.clubName || "";
  const roles = roleLine(user, t);
  const qrValue = user?.studentID != null ? String(user.studentID) : "";
  const duration = prefersReducedMotion ? "0ms" : "600ms";

  const surface = isDarkMode ? DARK_SURFACE : LIGHT_SURFACE;
  const background = isDarkMode ? DARK_BACKGROUND : LIGHT_BACKGROUND;
  const nameColor = isDarkMode ? "#FFFFFF" : "#10141D";
  const secondaryColor = isDarkMode ? "#8E99A5" : "#5B6573";
  const accentContent = isDarkMode ? "#FF5A5F" : "#C8161C";
  const frontGradient =
    showBeltRank && beltValue
      ? `linear-gradient(135deg, ${surface} 0%, ${mixHex(surface, beltColor, 0.3)} 46%, ${background} 100%)`
      : `linear-gradient(135deg, ${surface} 0%, ${mixHex(surface, surface, 0)} 46%, ${background} 100%)`;
  const backGradient = `linear-gradient(135deg, ${background} 0%, ${surface} 100%)`;

  const faceSx = {
    position: "absolute",
    inset: 0,
    borderRadius: "16px",
    overflow: "hidden",
    backfaceVisibility: "hidden",
    WebkitBackfaceVisibility: "hidden",
  };

  return (
    <Box
      component="button"
      type="button"
      onClick={() => setFlipped((open) => !open)}
      aria-label={t("landing_membership_flip")}
      aria-pressed={flipped}
      sx={{
        display: "block",
        width: "100%",
        maxWidth: 450,
        height: 220,
        mx: "auto",
        p: 0,
        border: isDarkMode ? 0 : "1px solid #7D8794",
        borderRadius: "16px",
        background: "transparent",
        cursor: "pointer",
        perspective: "1000px",
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        "&:focus-visible": {
          outline: `2px solid ${accentContent}`,
          outlineOffset: 4,
          borderRadius: "18px",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: "100%",
          transformStyle: "preserve-3d",
          transition: `transform ${duration} ease`,
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        <Box aria-hidden={flipped} sx={{ ...faceSx, background: frontGradient }}>
          <Box
            sx={{
              height: "100%",
              px: 2,
              py: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box sx={{ position: "relative", flexShrink: 0 }}>
                <SupabaseImageAvatar
                  user={user}
                  size={48}
                  refreshKey={imageRefreshKey}
                  sx={{
                    border: "none",
                    boxShadow: "none",
                    bgcolor: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(16,20,29,0.1)",
                    color: nameColor,
                    fontWeight: 700,
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    right: 0,
                    bottom: 0,
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    bgcolor: isDarkMode ? "#4ADE80" : "#166534",
                    border: `2px solid ${surface}`,
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                <Typography
                  sx={{
                    color: nameColor,
                    fontWeight: 700,
                    fontSize: 20,
                    lineHeight: 1.2,
                    letterSpacing: 0,
                    fontFamily: "inherit",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {user?.name || t("no_name_available")}
                </Typography>
                {roles ? (
                  <Typography
                    sx={{
                      mt: 0.25,
                      color: secondaryColor,
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: 1.3,
                      fontFamily: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {roles}
                  </Typography>
                ) : null}
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 1.5 }}>
              <Box sx={{ minWidth: 0, textAlign: "left" }}>
                {beltValue ? (
                  <RankBeltDisplay
                    belt={beltValue}
                    stripeCount={user?.beltStripe ?? 0}
                    width={100}
                    height={18}
                  />
                ) : null}
                {clubName ? (
                  <Typography
                    sx={{
                      mt: 0.75,
                      color: secondaryColor,
                      fontSize: 12,
                      fontWeight: 500,
                      lineHeight: 1.3,
                      fontFamily: "inherit",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {clubName}
                  </Typography>
                ) : null}
              </Box>
              {qrValue ? (
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    flexShrink: 0,
                    bgcolor: "#FFFFFF",
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <QRCode value={qrValue} size={52} renderAs="svg" />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>

        <Box
          aria-hidden={!flipped}
          sx={{
            ...faceSx,
            background: backGradient,
            transform: "rotateY(180deg)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {qrValue ? (
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: "12px",
                p: 1.5,
                display: "flex",
              }}
            >
              <QRCode value={qrValue} size={148} renderAs="svg" />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
}

export default LandingFlipMemberCard;
